import React, { useContext, useEffect, useRef, useCallback } from 'react';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  BodyText,
  Box,
  Button,
  Card,
  Modal,
} from '@chargify/custom-ui-components';
import { useToastMessages } from '@chargify/toasts';
import { useUndoCancelationService } from '../../../../hooks';
import { subscriptionsSelectors } from '../../../../machines';
import { GlobalStateContext } from '../../../../contexts';
import { undoCancelationSelectors } from '../../../../machines/undoCancelation';

type UndoCancelationProps = {
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: () => void;
  /**
   * Catalog id
   */
  catalogId: number;
  /**
   * Subscription id
   */
  subscriptionId: number;
};

const Content = ({
  onClose,
  displayMode,
  catalogId,
  subscriptionId,
}: UndoCancelationProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const modalRef = useRef(null);
  const toastService = useToastMessages();
  const undoCancelationService = useUndoCancelationService(
    catalogId,
    subscriptionId
  );

  const { subscriptionsService } = useContext(GlobalStateContext);
  const { subscription } = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionById(catalogId, subscriptionId),
      []
    )
  );
  const {
    undoCancelationError,
    undoCancelationLoading,
    undoCancelationSuccess,
  } = useSelector(
    undoCancelationService,
    undoCancelationSelectors.getUndoCancelationStatus
  );

  useEffect(() => {
    if (undoCancelationSuccess) {
      onClose();

      const toastMessage = t('cancel_subscription.cancelation_stopped');
      toastService.show({
        level: 'success',
        message: toastMessage,
      });
    }
  }, [undoCancelationSuccess, onClose, toastService, t]);

  const onUndoCancelation = () => {
    undoCancelationService.send({
      type: 'UNDO_CANCELATION',
    });
  };

  return (
    <>
      <Card
        border={displayMode === 'layer' ? 'sharp' : 'rounded'}
        ref={modalRef}
      >
        <Card.Header>
          <Modal.Title
            onClose={onClose}
            title={t('cancel_subscription.undo_cancelation_title')}
          />
        </Card.Header>
        <Card.Body>
          <BodyText>
            {t('cancel_subscription.stop_cancelation_process', {
              planName: subscription.product.name,
            })}
          </BodyText>
          {undoCancelationError && (
            <Box marginTop={5}>
              <Alert
                color="danger"
                title={t('cancel_subscription.unexpected_problem')}
              >
                <Alert.TextMessage>
                  {'cancel_subscription.unable_to_undo_cancelation'}
                </Alert.TextMessage>
              </Alert>
            </Box>
          )}
        </Card.Body>
        <Card.Footer>
          <Button.Group alignment="right">
            <Button
              color="secondary"
              label={t('cancel_subscription.close_window')}
              onClick={onClose}
              disabled={undoCancelationLoading}
            />
            <Button
              color="primary"
              label={t('cancel_subscription.keep_active_subscription')}
              disabled={undoCancelationError}
              isLoading={undoCancelationLoading}
              onClick={onUndoCancelation}
            />
          </Button.Group>
        </Card.Footer>
      </Card>
    </>
  );
};

export default Content;
